import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

interface SiteVisit {
  id: string;
  no: string;
  projek: string;
  pelanggan: string;
  lokasi: string;
  tarikh: string;
  masa: string;
  petugas: string;
  tujuanKunjungan: string;
  daerah: string;
  alamatLokasi: string;
  kontak: string;
  catatan: string;
  status: 'Draf' | 'Selesai' | 'Menunggu';
}

interface SiteVisitFormProps {
  visit?: SiteVisit;
  noKunjungan: string;
  projekList?: string[];
  customers?: any[];
  onSubmit: (data: SiteVisit) => void;
  onCancel: () => void;
}

export const SiteVisitForm = ({ visit, noKunjungan, projekList = [], customers = [], onSubmit, onCancel }: SiteVisitFormProps) => {
  const [formData, setFormData] = useState<Partial<SiteVisit>>(
    visit || {
      id: Date.now().toString(),
      no: noKunjungan,
      projek: '',
      pelanggan: '',
      lokasi: '',
      tarikh: new Date().toISOString().split('T')[0],
      masa: '',
      petugas: '',
      tujuanKunjungan: '',
      daerah: '',
      alamatLokasi: '',
      kontak: '',
      catatan: '',
      status: 'Draf',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchPelanggan, setSearchPelanggan] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectPelanggan = (namaSyarikat: string) => {
    setFormData(prev => ({ ...prev, pelanggan: namaSyarikat }));
    setSearchPelanggan('');
    if (errors.pelanggan) {
      setErrors(prev => ({ ...prev, pelanggan: '' }));
    }
  };

  const filteredPelanggan = customers.filter(c =>
    c.namaSyarikat.toLowerCase().includes(searchPelanggan.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.projek?.trim()) newErrors.projek = 'Nama projek diperlukan';
    if (!formData.pelanggan?.trim()) newErrors.pelanggan = 'Nama pelanggan diperlukan';
    if (!formData.lokasi?.trim()) newErrors.lokasi = 'Lokasi diperlukan';
    if (!formData.tarikh?.trim()) newErrors.tarikh = 'Tarikh diperlukan';
    if (!formData.petugas?.trim()) newErrors.petugas = 'Nama petugas diperlukan';
    if (!formData.tujuanKunjungan?.trim()) newErrors.tujuanKunjungan = 'Tujuan kunjungan diperlukan';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as SiteVisit);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Tambah Kunjungan Tapak</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombor Kunjungan (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nombor Kunjungan</label>
              <Input
                type="text"
                value={formData.no || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Nombor akan di-jana secara automatik</p>
            </div>

            {/* Nama Projek */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Projek *</label>
              <Input
                type="text"
                name="projek"
                value={(formData as any).projek || ''}
                onChange={handleChange}
                placeholder="Contoh: Pembinaan Bangunan ABC"
                className={errors.projek ? 'border-red-500' : ''}
              />
              {errors.projek && <p className="text-sm text-red-600">{errors.projek}</p>}
            </div>

            {/* Nama Pelanggan - Dropdown Carian */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Pelanggan *</label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Cari atau pilih pelanggan..."
                  value={searchPelanggan || (formData as any).pelanggan || ''}
                  onChange={(e) => setSearchPelanggan(e.target.value)}
                  className={errors.pelanggan ? 'border-red-500' : ''}
                />
                {(searchPelanggan || !(formData as any).pelanggan) && searchPelanggan !== (formData as any).pelanggan && (
                  <div className="absolute top-full left-0 right-0 border border-slate-300 border-t-0 rounded-b-lg bg-white shadow-lg z-10 max-h-48 overflow-y-auto">
                    {filteredPelanggan.length > 0 ? (
                      filteredPelanggan.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectPelanggan(c.namaSyarikat)}
                          className="w-full text-left px-4 py-2 hover:bg-slate-100 border-b border-slate-200 last:border-b-0"
                        >
                          <div className="font-medium text-slate-900">{c.namaSyarikat}</div>
                          <div className="text-xs text-slate-500">{c.email} • {c.telp}</div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        Tiada pelanggan ditemui
                      </div>
                    )}
                  </div>
                )}
                {(formData as any).pelanggan && !searchPelanggan && (
                  <div className="absolute top-full left-0 right-0 border border-slate-300 border-t-0 rounded-b-lg bg-white shadow-lg z-10 p-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, pelanggan: '' }));
                        setSearchPelanggan('');
                      }}
                      className="w-full text-left text-sm text-red-600 hover:text-red-700 py-1"
                    >
                      Bersihkan Pilihan
                    </button>
                  </div>
                )}
              </div>
              {errors.pelanggan && <p className="text-sm text-red-600">{errors.pelanggan}</p>}
            </div>

            {/* Daerah */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Daerah/Wilayah</label>
              <Input
                type="text"
                name="daerah"
                value={(formData as any).daerah || ''}
                onChange={handleChange}
                placeholder="Contoh: Kuala Lumpur, Selangor"
              />
            </div>

            {/* Lokasi Singkat */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Lokasi (Singkat) *</label>
              <Input
                type="text"
                name="lokasi"
                value={(formData as any).lokasi || ''}
                onChange={handleChange}
                placeholder="Contoh: Jln Merdeka, KL"
                className={errors.lokasi ? 'border-red-500' : ''}
              />
              {errors.lokasi && <p className="text-sm text-red-600">{errors.lokasi}</p>}
            </div>

            {/* Alamat Lokasi Lengkap */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Alamat Lokasi Lengkap</label>
              <textarea
                name="alamatLokasi"
                value={(formData as any).alamatLokasi || ''}
                onChange={handleChange}
                placeholder="Alamat lengkap lokasi kunjungan"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Tarikh */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Tarikh *</label>
              <Input
                type="date"
                name="tarikh"
                value={formData.tarikh || ''}
                onChange={handleChange}
                className={errors.tarikh ? 'border-red-500' : ''}
              />
              {errors.tarikh && <p className="text-sm text-red-600">{errors.tarikh}</p>}
            </div>

            {/* Masa */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Masa Kunjungan</label>
              <Input
                type="time"
                name="masa"
                value={(formData as any).masa || ''}
                onChange={handleChange}
              />
            </div>

            {/* Nama Petugas */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Petugas Kunjungan *</label>
              <Input
                type="text"
                name="petugas"
                value={(formData as any).petugas || ''}
                onChange={handleChange}
                placeholder="Contoh: Ahmad Bin Abdullah"
                className={errors.petugas ? 'border-red-500' : ''}
              />
              {errors.petugas && <p className="text-sm text-red-600">{errors.petugas}</p>}
            </div>

            {/* Kontak Petugas */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kontak Petugas</label>
              <Input
                type="tel"
                name="kontak"
                value={(formData as any).kontak || ''}
                onChange={handleChange}
                placeholder="Contoh: 012-3456789"
              />
            </div>

            {/* Tujuan Kunjungan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Tujuan Kunjungan *</label>
              <select
                name="tujuanKunjungan"
                value={(formData as any).tujuanKunjungan || ''}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.tujuanKunjungan ? 'border-red-500' : 'border-slate-300'
                }`}
              >
                <option value="">Pilih Tujuan Kunjungan</option>
                <option value="Pemeriksaan Kemajuan">Pemeriksaan Kemajuan</option>
                <option value="Inspeksi Kualitas">Inspeksi Kualitas</option>
                <option value="Resolusi Masalah">Resolusi Masalah</option>
                <option value="Pengukuran">Pengukuran</option>
                <option value="Dokumentasi">Dokumentasi</option>
                <option value="Diskusi Teknis">Diskusi Teknis</option>
                <option value="Lainnya">Lainnya</option>
              </select>
              {errors.tujuanKunjungan && <p className="text-sm text-red-600">{errors.tujuanKunjungan}</p>}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={formData.status || 'Draf'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Draf">Draf</option>
                <option value="Selesai">Selesai</option>
                <option value="Menunggu">Menunggu</option>
              </select>
            </div>

            {/* Catatan/Pemerhatian */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Catatan / Pemerhatian</label>
              <textarea
                name="catatan"
                value={(formData as any).catatan || ''}
                onChange={handleChange}
                placeholder="Catatan penting dari kunjungan, pemerhatian, atau tindakan lanjut yang diperlukan..."
                rows={4}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Tutup
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};